import { useMemo } from 'react';
import {
  Box,
  Text,
  TextInput,
  Checkbox,
  SelectInput,
  DatePicker,
  Link,
  Button,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import {
  GenderOption,
  FormFields,
} from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { CustomFields } from './components/CustomFields';
import { getPersonalDetailsValidationSchema } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/utils';
import { genderOptions } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/constants';
import {
  FormHeading,
  FormFieldsWrapper,
  StyledForm,
  FormFooterWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';
import { useAdditionalDetailsStep } from './hooks/useAdditionalDetailsStep';

export const PersonalDetails = () => {
  const history = useHistory();
  const { shouldShowStatutoryDetailsStep } = useSavedEmployeeData();
  const { shouldShowAdditionalDetailsStep } = useAdditionalDetailsStep();
  const { register, validate, formData } = useFormWithProvider<FormFields>();

  const handleContinue = async () => {
    if (!validate(getPersonalDetailsValidationSchema())) {
      return;
    }
    history.push(
      shouldShowAdditionalDetailsStep
        ? routePaths.employeeOnboarding.employeeSelfOnboarding.additionalDetails
        : routePaths.employeeOnboarding.employeeSelfOnboarding.reviewDetails,
    );
  };

  const handleBack = () => {
    const pathname = shouldShowStatutoryDetailsStep
      ? routePaths.employeeOnboarding.employeeSelfOnboarding.statutoryDetails
      : routePaths.employeeOnboarding.employeeSelfOnboarding.bankDetails;
    history.push(pathname);
  };

  const isFormValid = useMemo(
    () => validate(getPersonalDetailsValidationSchema(), { sideEffects: false }),
    [validate],
  );

  return (
    <StyledForm>
      <FormFieldsWrapper>
        <FormHeading
          title="Personal Details"
          description="Please provide your personal details to complete your profile."
        />
        <BorderedBox>
          <TextInput
            {...register('phoneNumber')}
            label="Phone Number"
            placeholder="Enter Phone Number"
            size="medium"
            prefix="+91"
          />

          <Box>
            <Text
              size="small"
              weight="medium"
              color="surface.text.gray.muted"
              marginBottom="spacing.3">
              Connect to WhatsApp
            </Text>
            <Checkbox
              {...register('enableWhatsapp', 'checkbox')}
              size="medium"
              isChecked={formData.enableWhatsapp}>
              Enable WhatsApp for my phone number
            </Checkbox>
            <Text size="small" color="surface.text.gray.muted" marginLeft="spacing.7">
              Claim reimbursements and get monthly payslips right in your WhatsApp account. Learn
              more about
              <Link
                href="https://razorpay.com/docs/payroll/integrations/whatsapp/"
                target="_blank"
                marginLeft="spacing.2"
                size="small"
                color="primary">
                WhatsApp integration.
              </Link>
            </Text>
          </Box>

          <Dropdown selectionType="single">
            <SelectInput
              {...register('gender', 'select')}
              label="Gender"
              placeholder="Select Gender"
              isRequired
              necessityIndicator="required"
            />
            <DropdownOverlay>
              <ActionList>
                {genderOptions.map((option: GenderOption) => (
                  <ActionListItem
                    key={option.value}
                    title={option.title}
                    value={option.value}
                    isSelected={formData.gender?.[0] === option.value}
                  />
                ))}
              </ActionList>
            </DropdownOverlay>
          </Dropdown>

          <DatePicker
            {...register('dob', 'date')}
            selectionType="single"
            label="Date of Birth"
            format="DD/MM/YYYY"
            size="medium"
            necessityIndicator="required"
            isRequired
            maxDate={new Date()} //future dates not allowed
          />

          <CustomFields register={register} formData={formData} />
        </BorderedBox>
      </FormFieldsWrapper>

      <FormFooterWrapper>
        <Button variant="tertiary" size="medium" onClick={handleBack}>
          Back
        </Button>
        <Button variant="primary" size="medium" onClick={handleContinue} isDisabled={!isFormValid}>
          Continue
        </Button>
      </FormFooterWrapper>
    </StyledForm>
  );
};
